/**
 * BreachListPage
 * Route: /admin/breach
 *
 * List view for all breaches — DPO/admin view.
 */

import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ShieldAlert,
  Plus,
  ArrowLeft,
  Loader2,
  AlertTriangle,
  Clock,
  Users,
  ExternalLink,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { listBreaches, listOverdueBreaches } from "@/services/dpdpApi";

function getSeverityColor(severity) {
  switch (severity) {
    case "critical": return "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200";
    case "high": return "bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200";
    case "medium": return "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200";
    case "low": return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    default: return "bg-gray-100 text-gray-800";
  }
}

function getStatusColor(status) {
  switch (status) {
    case "detected": return "bg-gray-500 text-white";
    case "investigating": return "bg-blue-500 text-white";
    case "contained": return "bg-orange-500 text-white";
    case "notified": return "bg-amber-500 text-white";
    case "resolved": return "bg-green-500 text-white";
    case "closed": return "bg-emerald-600 text-white";
    default: return "bg-gray-500 text-white";
  }
}

export default function BreachListPage() {
  const navigate = useNavigate();
  const [breaches, setBreaches] = useState([]);
  const [overdue, setOverdue] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [listData, overdueData] = await Promise.all([
        listBreaches().catch(() => ({ items: [], total: 0 })),
        listOverdueBreaches().catch(() => []),
      ]);
      setBreaches(listData.items || []);
      setTotal(listData.total || 0);
      setOverdue(overdueData || []);
    } catch (err) {
      toast.error("Failed to load breaches");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-red-950/10 to-slate-900">
      {/* Header */}
      <header className="border-b border-white/10 bg-black/20 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-5xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            className="text-white hover:bg-white/10"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="font-display text-lg font-bold text-white flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-red-400" />
              Breach Notifications
            </h1>
            <p className="text-xs text-white/50">DPDP Act 2023 — Section 8(6)</p>
          </div>
          <Button
            className="bg-red-600 hover:bg-red-700"
            onClick={() => navigate("/admin/breach/report")}
          >
            <Plus className="h-4 w-4 mr-2" />
            Report Breach
          </Button>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-6 space-y-6">
        {/* Overdue summary */}
        {overdue.length > 0 && (
          <div className="rounded-lg bg-red-950/30 border border-red-500/30 p-4 flex gap-3 items-center">
            <AlertTriangle className="h-5 w-5 text-red-400 flex-shrink-0" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-red-300">
                {overdue.length} Overdue Breach{overdue.length > 1 ? "es" : ""}
              </p>
              <p className="text-xs text-red-200/60">
                DPB notification deadline has passed without notification
              </p>
            </div>
            <Badge className="bg-red-600 text-white">{overdue.length}</Badge>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-4 gap-4">
          {[
            { label: "Total Breaches", value: total, icon: ShieldAlert, color: "text-white" },
            { label: "Overdue", value: overdue.length, icon: AlertTriangle, color: "text-red-400" },
            { label: "Investigating", value: breaches.filter(b => b.status === "investigating").length, icon: Clock, color: "text-blue-400" },
            { label: "Resolved", value: breaches.filter(b => ["resolved", "closed"].includes(b.status)).length, icon: ShieldAlert, color: "text-green-400" },
          ].map((stat) => (
            <Card key={stat.label}>
              <CardContent className="py-4 text-center">
                <stat.icon className={`h-5 w-5 mx-auto mb-2 ${stat.color}`} />
                <div className="text-2xl font-bold text-white">{loading ? "—" : stat.value}</div>
                <div className="text-xs text-white/50">{stat.label}</div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Breach list */}
        <Card>
          <CardHeader className="flex-row items-center justify-between space-y-0">
            <CardTitle className="text-base">All Breaches</CardTitle>
            <Button
              size="sm"
              variant="outline"
              className="text-xs"
              onClick={() => navigate("/admin/breach/drill")}
            >
              <ShieldAlert className="h-3 w-3 mr-1" />
              Run Drill
            </Button>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-white/40" />
              </div>
            ) : breaches.length === 0 ? (
              <div className="text-center py-12">
                <ShieldAlert className="h-12 w-12 text-white/20 mx-auto mb-4" />
                <p className="text-white/50 text-sm">No breaches reported</p>
                <Button
                  variant="outline"
                  className="mt-4"
                  onClick={() => navigate("/admin/breach/report")}
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Report First Breach
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {breaches.map((breach) => (
                  <button
                    key={breach.breach_id}
                    onClick={() => navigate(`/admin/breach/${breach.breach_id}`)}
                    className="w-full flex items-center gap-4 p-4 rounded-lg bg-white/5 hover:bg-white/10 border border-white/5 hover:border-white/20 transition-all text-left group"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={getSeverityColor(breach.severity)}>{breach.severity}</Badge>
                        <Badge className={getStatusColor(breach.status)}>{breach.status}</Badge>
                        {breach.auto_created && (
                          <Badge variant="outline" className="border-purple-500/50 text-purple-400 text-xs">
                            auto
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm font-medium text-white truncate">
                        {breach.breach_type.replace(/_/g, " ")}
                      </p>
                      <div className="flex items-center gap-4 mt-1 text-xs text-white/40">
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" />
                          {new Date(breach.detected_at).toLocaleDateString()}
                        </span>
                        <span className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          {breach.estimated_affected_users.toLocaleString()} users
                        </span>
                        <span className={breach.dpb_overdue ? "text-red-400 font-semibold" : ""}>
                          {breach.dpb_overdue ? "OVERDUE" : `Due ${new Date(breach.due_by_dpb).toLocaleDateString()}`}
                        </span>
                      </div>
                    </div>
                    <ExternalLink className="h-4 w-4 text-white/30 group-hover:text-white/60 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
